import Analytics from '@/utils/analytics';
import React from 'react';
import {
  Animated,
  Image,
  StatusBar,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import { Disclosures } from './disclosures';
import { KeyboardAwareScrollView } from './with-keyboard-aware';
import {
  LARGE_LAYOUT_CONTENT_MAX_WIDTH,
  SCREEN_PADDING,
  TYPOGRAPHY,
} from '@/styles';
import { openWebBrowserURL } from '@/utils/navigation';
import { useAuthStackContext } from '@/contexts/auth-stack-context';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

interface Props {
  children: React.ReactNode;
  title?: string;
}

export const PEPPER_WEBSITE = 'https://www.usepepper.com/';

export const AuthPageContainer: React.FC<Props> = ({ children, title }) => {
  const { headerHeight } = useAuthStackContext();
  const { isSmallLayout } = useLayoutContext();
  const { bottom } = useSafeAreaInsets();
  const [styles, theme] = useStyles();

  return (
    <>
      <StatusBar backgroundColor={theme.colors.authBackground} />
      <KeyboardAwareScrollView
        alwaysBounceVertical={false}
        contentContainerStyle={styles.scrollViewContentContainer}
        keyboardShouldPersistTaps="handled"
      >
        <Animated.View
          style={[
            styles.safeAreaView,
            isSmallLayout
              ? styles.smallLayoutSafeAreaView
              : styles.largeLayoutSafeAreaView,
            { paddingTop: headerHeight },
          ]}
        >
          {title ? (
            <Text style={[TYPOGRAPHY.h2, styles.compactTitle]}>{title}</Text>
          ) : null}
          {children}
        </Animated.View>
        <TouchableOpacity
          onPress={() => {
            Analytics.track('powered_by_pepper_image.press');
            openWebBrowserURL(PEPPER_WEBSITE);
          }}
          style={styles.pepperLinkButton}
          testID="powered-by-pepper"
        >
          <Image
            alt="powered by pepper"
            source={require('@assets/images/powered-by-pepper.png')}
            style={styles.poweredByPepper}
          />
        </TouchableOpacity>
        <View
          style={[
            styles.accountFooter,
            { paddingBottom: Math.max(bottom, 16) },
          ]}
        >
          <Disclosures />
        </View>
      </KeyboardAwareScrollView>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  accountFooter: {
    paddingTop: 16,
  },
  compactTitle: {
    marginBottom: 12,
    textAlign: 'center',
  },
  largeLayoutSafeAreaView: {
    marginHorizontal: 'auto',
    maxWidth: LARGE_LAYOUT_CONTENT_MAX_WIDTH,
    width: '100%',
  },
  pepperLinkButton: {
    paddingHorizontal: SCREEN_PADDING,
    paddingTop: 16,
  },
  poweredByPepper: {
    height: 20,
    width: 150,
  },
  safeAreaView: {
    flex: 1,
  },
  scrollViewContentContainer: {
    alignItems: 'center',
    backgroundColor: theme.colors.authBackground,
    flexGrow: 1,
    paddingHorizontal: SCREEN_PADDING,
  },
  smallLayoutSafeAreaView: {
    width: '100%',
  },
}));
